/**
 * YouTube Modal Player
 * Displays YouTube videos in a lightbox modal overlay
 *
 * Usage:
 * Add to any element:
 * - data-youtube-trigger: Marks the clickable element
 * - data-youtube-id: The YouTube video ID (e.g., "dQw4w9WgXcQ")
 *
 * Optional attributes:
 * - data-youtube-autoplay: "true" or "false" (default: "true")
 * - data-youtube-mute: "true" or "false" (default: "true")
 * - data-youtube-bg-color: Background color (default: "rgba(0, 0, 0, 0.9)")
 *
 * Example:
 * <div data-youtube-trigger data-youtube-id="dQw4w9WgXcQ">
 *   <img src="thumbnail.jpg" alt="Play video" />
 * </div>
 */

export class YouTubeModal {
  private wrapper: HTMLElement | null = null;
  private videoContainer: HTMLElement | null = null;
  private iframe: HTMLIFrameElement | null = null;
  private closeButton: HTMLButtonElement | null = null;
  private previouslyFocused: HTMLElement | null = null;
  private videoId: string;
  private autoplay: boolean;
  private mute: boolean;
  private backgroundColor: string;

  constructor(
    videoId: string,
    options: {
      autoplay?: boolean;
      mute?: boolean;
      backgroundColor?: string;
    } = {}
  ) {
    this.videoId = videoId;
    this.autoplay = options.autoplay ?? true;
    this.mute = options.mute ?? true;
    this.backgroundColor = options.backgroundColor ?? 'rgba(0, 0, 0, 0.9)';
    this.previouslyFocused = document.activeElement as HTMLElement;
  }

  public open(): void {
    this.createWrapper();
    this.createVideoContainer();
    this.createVideo();
    this.createCloseButton();
    this.setupEventListeners();
    this.appendToDOM();
    this.setInitialFocus();
  }

  private createWrapper(): void {
    this.wrapper = document.createElement('div');
    Object.assign(this.wrapper.style, {
      position: 'fixed',
      inset: '0',
      zIndex: '9999',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: this.backgroundColor,
    });

    this.wrapper.setAttribute('role', 'dialog');
    this.wrapper.setAttribute('aria-label', 'YouTube video player');
    this.wrapper.setAttribute('data-youtube-modal', 'wrapper');
  }

  private createVideoContainer(): void {
    this.videoContainer = document.createElement('div');
    this.videoContainer.setAttribute('data-youtube-modal', 'container');

    // Enforce 16:9 aspect ratio
    Object.assign(this.videoContainer.style, {
      position: 'relative',
      width: '90vw',
      maxWidth: '1200px',
      aspectRatio: '16/9',
      background: '#000',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '4px',
      overflow: 'hidden',
    });
  }

  private createVideo(): void {
    this.iframe = document.createElement('iframe');
    this.iframe.setAttribute('data-youtube-modal', 'iframe');
    Object.assign(this.iframe.style, {
      width: '100%',
      height: '100%',
      border: 'none',
      borderRadius: '4px',
      position: 'absolute',
      top: '0',
      left: '0',
    });

    // Build YouTube URL with parameters
    const params = new URLSearchParams({
      enablejsapi: '1',
      origin: window.location.origin,
      ...(this.autoplay && { autoplay: '1' }),
      ...(this.mute && { mute: '1' }),
    });

    this.iframe.src = `https://www.youtube.com/embed/${this.videoId}?${params.toString()}`;
    this.iframe.allow =
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
    this.iframe.allowFullscreen = true;
    this.iframe.setAttribute('title', 'YouTube video player');

    if (this.videoContainer && this.iframe) {
      this.videoContainer.appendChild(this.iframe);
    }
    if (this.wrapper && this.videoContainer) {
      this.wrapper.appendChild(this.videoContainer);
    }
  }

  private createCloseButton(): void {
    this.closeButton = document.createElement('button');
    this.closeButton.setAttribute('data-youtube-modal', 'close');
    Object.assign(this.closeButton.style, {
      position: 'absolute',
      top: '48px',
      right: '22px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '0',
      zIndex: '10000',
    });
    this.closeButton.setAttribute('aria-label', 'Close video player');
    this.closeButton.innerHTML = `<svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clip-path="url(#clip0_youtube_modal)">
        <path opacity="0.4" d="M3.99996 4.03523e-05L8.94971 4.94979L13.8995 4.03523e-05L17.435 3.53557L12.4852 8.48532L17.435 13.4351L13.8995 16.9706L8.94971 12.0209L3.99996 16.9706L0.464426 13.4351L5.41417 8.48532L0.464426 3.53557L3.99996 4.03523e-05Z" fill="black"/>
        <path d="M3.99996 1.41435L8.94971 6.36409L13.8995 1.41435L16.0208 3.53567L11.071 8.48541L16.0208 13.4352L13.8995 15.5565L8.94971 10.6067L3.99996 15.5565L1.87864 13.4352L6.82839 8.48541L1.87864 3.53567L3.99996 1.41435Z" fill="white"/>
      </g>
      <defs>
        <clipPath id="clip0_youtube_modal">
          <rect width="18" height="17" fill="white"/>
        </clipPath>
      </defs>
    </svg>`;

    this.closeButton.onclick = () => this.cleanup();
    this.wrapper?.appendChild(this.closeButton);
  }

  private setupEventListeners(): void {
    if (!this.wrapper) return;

    // Close on backdrop click
    this.wrapper.addEventListener('click', (e) => {
      if (e.target === this.wrapper) this.cleanup();
    });

    // Close on Escape key
    document.addEventListener('keydown', this.handleEscape);

    // Trap focus within modal
    document.addEventListener('keydown', this.handleTab);
  }

  private appendToDOM(): void {
    if (!this.wrapper) return;
    document.body.appendChild(this.wrapper);
  }

  private setInitialFocus(): void {
    this.iframe?.focus();
  }

  private handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') this.cleanup();
  };

  private handleTab = (e: KeyboardEvent) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      this.iframe?.focus();
    }
  };

  public cleanup(): void {
    if (this.wrapper) {
      this.wrapper.remove();
    }

    document.removeEventListener('keydown', this.handleEscape);
    document.removeEventListener('keydown', this.handleTab);

    // Restore focus
    this.previouslyFocused?.focus();
  }
}

/**
 * YouTube Modal Controller
 * Automatically initializes all YouTube triggers on the page
 */
export class YouTubeModalController {
  private triggers: NodeListOf<HTMLElement> | null = null;

  public init(): void {
    this.triggers = document.querySelectorAll('[data-youtube-trigger]');

    if (!this.triggers || this.triggers.length === 0) {
      return;
    }

    this.triggers.forEach((trigger) => {
      this.setupTrigger(trigger);
    });
  }

  private setupTrigger(trigger: HTMLElement): void {
    const videoId = trigger.getAttribute('data-youtube-id');

    if (!videoId) {
      console.error('YouTube trigger missing data-youtube-id attribute:', trigger);
      return;
    }

    // Get optional attributes
    const autoplay = trigger.getAttribute('data-youtube-autoplay') !== 'false';
    const mute = trigger.getAttribute('data-youtube-mute') !== 'false';
    const backgroundColor = trigger.getAttribute('data-youtube-bg-color') || 'rgba(0, 0, 0, 0.9)';

    // Add click handler
    trigger.addEventListener('click', (e) => {
      e.preventDefault();

      const modal = new YouTubeModal(videoId, {
        autoplay,
        mute,
        backgroundColor,
      });

      modal.open();
    });

    // Add keyboard handler for accessibility
    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();

        const modal = new YouTubeModal(videoId, {
          autoplay,
          mute,
          backgroundColor,
        });

        modal.open();
      }
    });

    // Make trigger focusable if not already
    if (!trigger.hasAttribute('tabindex')) {
      trigger.setAttribute('tabindex', '0');
    }

    // Add role if not a button or link
    if (trigger.tagName !== 'BUTTON' && trigger.tagName !== 'A' && !trigger.hasAttribute('role')) {
      trigger.setAttribute('role', 'button');
    }

    // Add cursor pointer style
    trigger.style.cursor = 'pointer';
  }
}
